import { Input, Textarea, Switch, SegmentedControl } from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import type { ItemType } from '@/api/capacities';

export interface ModelFormValues {
  item_type: ItemType;
  category: string;
  model: string;
  name: string;
  description: string;
  is_active: boolean;
}

const TYPE_ITEMS = [
  { value: 'equipment', label: 'Equipment' },
  { value: 'component', label: 'Component' },
];

/** Setter accepting any superset of `ModelFormValues` (the page owns extra state). */
export type ModelFieldSetter = <K extends keyof ModelFormValues>(
  key: K,
  value: ModelFormValues[K],
) => void;

export interface ModelFormFieldsProps {
  values: ModelFormValues;
  onChange: ModelFieldSetter;
  errors: { model: boolean; name: boolean; category: boolean };
  /** Mark a required field touched (validate-on-blur). */
  onTouch?: (key: 'model' | 'name' | 'category') => void;
}

/**
 * The scalar field block of the Model form (type · category · model · name ·
 * description · active). Extracted so the page file stays lean; the parent owns
 * state and validation.
 */
export function ModelFormFields({ values, onChange, errors, onTouch }: ModelFormFieldsProps) {
  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <Field label="Type">
        {() => (
          <SegmentedControl
            items={TYPE_ITEMS}
            value={values.item_type}
            onValueChange={(v) => onChange('item_type', v as ItemType)}
            aria-label="Model type"
          />
        )}
      </Field>
      <Field label="Category" required error={errors.category ? 'Required' : undefined}>
        {(p) => (
          <Input
            {...p}
            value={values.category}
            invalid={errors.category}
            onBlur={() => onTouch?.('category')}
            onChange={(e) => onChange('category', e.target.value)}
          />
        )}
      </Field>
      <Field label="Model" required error={errors.model ? 'Required' : undefined}>
        {(p) => (
          <Input
            {...p}
            value={values.model}
            invalid={errors.model}
            onBlur={() => onTouch?.('model')}
            onChange={(e) => onChange('model', e.target.value)}
          />
        )}
      </Field>
      <Field label="Name" required error={errors.name ? 'Required' : undefined}>
        {(p) => (
          <Input
            {...p}
            value={values.name}
            invalid={errors.name}
            onBlur={() => onTouch?.('name')}
            onChange={(e) => onChange('name', e.target.value)}
          />
        )}
      </Field>
      <Field label="Description">
        {(p) => (
          <Textarea
            {...p}
            rows={3}
            value={values.description}
            onChange={(e) => onChange('description', e.target.value)}
          />
        )}
      </Field>
      <Field label="Active" description="Inactive models are hidden from item pickers.">
        {() => <Switch checked={values.is_active} onCheckedChange={(v) => onChange('is_active', v)} />}
      </Field>
    </div>
  );
}
